import { Injectable, isDevMode } from '@angular/core';
import {
  MoveStart,
  MoveTypes,
  CropperPosition,
  HammerManager,
  HammerStatic,
} from '../interfaces/image-cropper.interfaces';

@Injectable({
  providedIn: 'root',
})
export class GestureService {
  private readonly Hammer: HammerStatic =
    typeof window !== 'undefined' ? (window as unknown as { Hammer: HammerStatic }).Hammer : null;

  /**
   * Initializes move start object
   */
  public initMoveStart(): MoveStart {
    return {
      active: false,
      type: null,
      position: null,
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 0,
      clientX: 0,
      clientY: 0,
    };
  }

  /**
   * Activates pinch gesture using HammerJS
   */
  public activatePinchGesture(wrapperElement: HTMLElement): void {
    if (this.Hammer) {
      const hammer: HammerManager = new this.Hammer(wrapperElement);
      hammer.get('pinch').set({ enable: true });
    } else if (isDevMode()) {
      // eslint-disable-next-line no-console
      console.warn("[NgxImageCropper] Could not find HammerJS - Pinch Gesture won't work");
    }
  }

  /**
   * Starts move operation
   */
  public startMove(event: Event, moveType: MoveTypes, position: string | null, cropper: CropperPosition): MoveStart {
    if (event.preventDefault) {
      event.preventDefault();
    }
    return {
      active: true,
      type: moveType,
      position,
      clientX: this.getClientX(event),
      clientY: this.getClientY(event),
      ...cropper,
    };
  }
  
  /**
   * Handles keyboard navigation
   */
  public handleKeyboardNavigation(
    event: KeyboardEvent,
    stepSize: number
  ): { moveType: MoveTypes; position: string; moveEvent: unknown } | null {
    const keyboardWhiteList: string[] = ['ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft'];
    if (!keyboardWhiteList.includes(event.key)) {
      return null;
    }

    const moveType: MoveTypes = event.shiftKey ? MoveTypes.RESIZE : MoveTypes.MOVE;
    const position: string = event.altKey
      ? this.getInvertedPositionForKey(event.key)
      : this.getPositionForKey(event.key);
    const moveEvent: unknown = this.getEventForKey(event.key, stepSize);

    event.preventDefault();
    event.stopPropagation();

    return { moveType, position, moveEvent };
  }

  /**
   * Changes keyboard step size
   */
  public changeKeyboardStepSize(event: KeyboardEvent, currentStepSize: number): number {
    if (event.key >= '1' && event.key <= '9') {
      return +event.key;
    }
    return currentStepSize;
  }

  /**
   * Gets position for arrow key
   */
  private getPositionForKey(key: string): string {
    switch (key) {
      case 'ArrowUp':
        return 'top';
      case 'ArrowRight':
        return 'right';
      case 'ArrowDown':
        return 'bottom';
      case 'ArrowLeft':
      default:
        return 'left';
    }
  }

  /**
   * Gets inverted position for arrow key (with Alt key)
   */
  private getInvertedPositionForKey(key: string): string {
    switch (key) {
      case 'ArrowUp':
        return 'bottom';
      case 'ArrowRight':
        return 'left';
      case 'ArrowDown':
        return 'top';
      case 'ArrowLeft':
      default:
        return 'right';
    }
  }

  /**
   * Gets event object for arrow key movement
   */
  private getEventForKey(key: string, stepSize: number): { clientX: number; clientY: number } {
    switch (key) {
      case 'ArrowUp':
        return { clientX: 0, clientY: stepSize * -1 };
      case 'ArrowRight':
        return { clientX: stepSize, clientY: 0 };
      case 'ArrowDown':
        return { clientX: 0, clientY: stepSize };
      case 'ArrowLeft':
      default:
        return { clientX: stepSize * -1, clientY: 0 };
    }
  }

  /**
   * Gets client X coordinate from event
   */
  public getClientX(event: Event): number {
    const touchEvent: TouchEvent = event as TouchEvent;
    return (touchEvent.touches?.[0] ? touchEvent.touches[0].clientX : (event as MouseEvent).clientX) || 0;
  }

  /**
   * Gets client Y coordinate from event
   */
  public getClientY(event: Event): number {
    const touchEvent: TouchEvent = event as TouchEvent;
    return (touchEvent.touches?.[0] ? touchEvent.touches[0].clientY : (event as MouseEvent).clientY) || 0;
  }

  /**
   * Stops move operation
   */
  public stopMove(moveStart: MoveStart): MoveStart {
    return {
      ...moveStart,
      active: false,
    };
  }
}
